import { PrimVoicesProvider, usePrimVoices, WebSocketClientConfig } from "primvoices-react";
import { useEffect, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import clsx from "clsx";

import { AgentMonitoring } from "./AgentMonitoring";
import { AdminCanaryRoutingToggle } from "./AdminCanaryRoutingToggle";
import { Alert, AlertDescription, Button, InputField, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { useAppDispatch, useAppSelector } from "../../redux";
import { API_URL } from "../../config";
import { Environment, environmentActions } from "../../redux/reducers/environmentSlice";
import { Release } from "../../redux/reducers/releaseSlice";
import { AgentFunction } from "../../redux/reducers/agentFunctionSlice";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAuth } from "../../hooks/useAuth";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { getGradientForName } from "../../utils/gradients";

type DebugSessionResponse = {
  data: {
    url: string;
    parameters: Record<string, string>;
  };
};

type EngineColor = "blue" | "green";

// The debug-session endpoint resolves the WebSocket URL to a specific
// blue/green engine color (path contains /agents-blue or /agents-green).
// Surface that so admins can confirm which color a session actually hit.
const parseEngineColor = (url: string | undefined): EngineColor | null => {
  if (!url) return null;
  if (url.includes("/agents-blue")) return "blue";
  if (url.includes("/agents-green")) return "green";
  return null;
};

type EnvDropdownProps = {
  environments: Environment[];
  value: string;
  onChange: (id: string) => void;
};

const EnvDropdown = ({ environments, value, onChange }: EnvDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = environments.find((env) => env.id === value);
  const selectedGradient = selected ? getGradientForName(selected.name) : null;

  return (
    <View className="relative flex-1 min-w-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
      >
        <View className="flex items-center gap-2 min-w-0">
          {selected && selectedGradient ? (
            <View
              className={clsx(
                "w-5 h-5 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0",
                selectedGradient.from,
                selectedGradient.to,
                selectedGradient.darkFrom,
                selectedGradient.darkTo,
              )}
            >
              <span className="text-xs font-bold text-white">{selected.name.substring(0, 1).toUpperCase()}</span>
            </View>
          ) : (
            <Icon
              name="layers"
              size={14}
              className="text-gray-500 dark:text-gray-400 flex-shrink-0"
            />
          )}
          <span className="truncate">{selected?.name || "Select"}</span>
        </View>
        <Icon
          name={isOpen ? "chevron-up" : "chevron-down"}
          size={14}
          className="text-gray-400 flex-shrink-0"
        />
      </button>
      {isOpen && (
        <>
          <View
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <View className="absolute z-50 top-full left-0 mt-1 min-w-[180px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
            {environments.map((env) => {
              const gradient = getGradientForName(env.name);
              return (
                <button
                  key={env.id}
                  onClick={() => {
                    onChange(env.id);
                    setIsOpen(false);
                  }}
                  className={clsx(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                    value === env.id
                      ? "bg-brand/10 text-brand dark:text-brand-light"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                  )}
                >
                  <View
                    className={clsx(
                      "w-4 h-4 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0",
                      gradient.from,
                      gradient.to,
                      gradient.darkFrom,
                      gradient.darkTo,
                    )}
                  >
                    <span className="text-[0.5rem] font-bold text-white">{env.name.substring(0, 1).toUpperCase()}</span>
                  </View>
                  <span className="capitalize">{env.name}</span>
                </button>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
};

type ReleaseDropdownProps = {
  releases: Release[];
  functionsById: Record<string, AgentFunction | undefined>;
  value: string;
  onChange: (id: string) => void;
};

const releaseLabel = (release: Release, fn: AgentFunction | undefined) => {
  const fnName = fn?.displayName || fn?.name || (release.functionId ? release.functionId.slice(0, 8) : "—");
  const ts = formatDistanceToNow(new Date(release.createdAt), { addSuffix: true });
  return { fnName, ts };
};

const ReleaseDropdown = ({ releases, functionsById, value, onChange }: ReleaseDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = releases.find((r) => r.id === value);
  const selectedFn = selected?.functionId ? functionsById[selected.functionId] : undefined;
  const selectedLabel = selected ? releaseLabel(selected, selectedFn) : null;

  return (
    <View className="relative flex-1 min-w-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={releases.length === 0}
        className={clsx(
          "flex items-center justify-between gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors",
          releases.length === 0
            ? "bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
            : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300",
        )}
      >
        <View className="flex items-center gap-2 min-w-0">
          <Icon
            name="rocket"
            size={14}
            className="text-gray-500 dark:text-gray-400 flex-shrink-0"
          />
          {selectedLabel ? (
            <View className="flex flex-col min-w-0 text-left">
              <span className="truncate leading-tight">{selectedLabel.fnName}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate leading-tight">{selectedLabel.ts}</span>
            </View>
          ) : (
            <span className="truncate">{releases.length === 0 ? "No releases" : "Select release"}</span>
          )}
        </View>
        <Icon
          name={isOpen ? "chevron-up" : "chevron-down"}
          size={14}
          className="text-gray-400 flex-shrink-0"
        />
      </button>
      {isOpen && releases.length > 0 && (
        <>
          <View
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <View className="absolute z-50 top-full left-0 mt-1 min-w-[240px] max-h-72 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
            {releases.map((r) => {
              const fn = r.functionId ? functionsById[r.functionId] : undefined;
              const { fnName, ts } = releaseLabel(r, fn);
              return (
                <button
                  key={r.id}
                  onClick={() => {
                    onChange(r.id);
                    setIsOpen(false);
                  }}
                  className={clsx(
                    "w-full flex flex-col items-start gap-0.5 px-3 py-2 text-sm text-left transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0",
                    value === r.id
                      ? "bg-brand/10 text-brand dark:text-brand-light"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                  )}
                >
                  <View className="flex items-center gap-2 w-full">
                    <Icon
                      name="rocket"
                      size={12}
                      className="text-gray-400 flex-shrink-0"
                    />
                    <span className="truncate font-medium">{fnName}</span>
                    <span className="ml-auto text-[0.65rem] font-mono text-gray-400">{r.id.slice(0, 8)}</span>
                  </View>
                  <span className="text-xs text-gray-500 dark:text-gray-400 pl-5">{ts}</span>
                </button>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
};

type ReleaseDebuggerInternalProps = {
  isOpen: boolean;
  onClick: () => void;
  environments: Environment[];
  releasesInEnv: Release[];
  functionsById: Record<string, AgentFunction | undefined>;
  environmentId: string;
  releaseId: string;
  onChangeEnvironment: (id: string) => void;
  onChangeRelease: (id: string) => void;
  startError: string | null;
  isStarting: boolean;
  onRequestStart: () => void;
  canaryRouting: boolean;
  onChangeCanaryRouting: (value: boolean) => void;
  engineColor: EngineColor | null;
};

const ReleaseDebuggerInternal = ({
  isOpen,
  onClick,
  environments,
  releasesInEnv,
  functionsById,
  environmentId,
  releaseId,
  onChangeEnvironment,
  onChangeRelease,
  startError,
  isStarting,
  onRequestStart,
  canaryRouting,
  onChangeCanaryRouting,
  engineColor,
}: ReleaseDebuggerInternalProps) => {
  const [inputMessage, setInputMessage] = useState("");
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { disconnect, startListening, stopListening, sendTextEvent, isConnected, isListening, error: primVoicesError } =
    usePrimVoices();

  const selectedEnv = environments.find((env) => env.id === environmentId);
  const envName = selectedEnv?.name;
  const hasReleaseSelected = !!releaseId;

  const onClickStart = () => {
    if (!releaseId) return;
    setConnectionError(null);
    onRequestStart();
  };

  const onClickStop = async () => {
    try {
      setConnectionError(null);
      await stopListening();
      await disconnect();
      setInputMessage("");
    } catch (err) {
      console.error("Failed to stop conversation:", err);
    }
  };

  const onClickMic = async () => {
    try {
      if (isListening) {
        await stopListening();
      } else {
        await startListening();
      }
    } catch (err) {
      console.error("Failed to toggle microphone:", err);
    }
  };

  const onClickSendMessage = async () => {
    if (!inputMessage.trim()) return;
    try {
      const userMessage = inputMessage.trim();
      await sendTextEvent(userMessage);
      setInputMessage("");
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onClickSendMessage();
    }
  };

  return (
    <View
      className={clsx(
        "relative h-full bg-white dark:bg-gray-800 shadow-lg border-l border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out",
        isOpen ? "w-[500px]" : "w-[48px]",
        "hidden md:flex flex-col overflow-hidden",
      )}
    >
      {/* Collapsed State */}
      {!isOpen && (
        <View className="flex flex-col items-center h-full">
          <button
            type="button"
            onClick={onClick}
            aria-label="Expand debug sidebar"
            className="p-2 mt-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Icon
              name="chevron-left"
              size={18}
              className="text-gray-500 dark:text-gray-400"
            />
          </button>
          <Text className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-medium text-gray-700 dark:text-gray-300 -rotate-90 whitespace-nowrap">
            Debugger
          </Text>
        </View>
      )}

      {/* Expanded State */}
      {isOpen && (
        <View className="flex flex-col h-full w-full overflow-hidden">
          {/* Header Controls */}
          <View className="flex flex-col gap-3 py-3 px-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <View className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClick}
                aria-label="Collapse debug sidebar"
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
              >
                <Icon
                  name="chevron-right"
                  size={18}
                  className="text-gray-500 dark:text-gray-400"
                />
              </button>
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Debug Release</Text>
              {/* Blue/green engine color of the current or most recent session.
                  Deliberately unlabeled — meaningful to platform admins,
                  ignorable for everyone else. Persists after disconnect (the
                  attempt config keeps the last resolved URL) and is replaced
                  when the next session's routing resolves. */}
              {engineColor && (
                <View
                  className={clsx(
                    "ml-auto w-2 h-2 rounded-full flex-shrink-0",
                    engineColor === "blue" ? "bg-blue-500" : "bg-green-500",
                  )}
                />
              )}
            </View>

            <View className="flex flex-col gap-2">
              <EnvDropdown
                environments={environments}
                value={environmentId}
                onChange={onChangeEnvironment}
              />
              <ReleaseDropdown
                releases={releasesInEnv}
                functionsById={functionsById}
                value={releaseId}
                onChange={onChangeRelease}
              />
            </View>

            <View className="flex items-center gap-3">
              <button
                onClick={isConnected ? onClickMic : undefined}
                disabled={!isConnected}
                title={!isConnected ? "Start session to enable microphone" : isListening ? "Mute microphone" : "Unmute microphone"}
                className={clsx(
                  "flex items-center justify-center h-9 w-9 rounded-lg transition-colors flex-shrink-0 border",
                  !isConnected
                    ? "bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-40"
                    : isListening
                      ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                      : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700",
                )}
              >
                <Icon
                  name={isListening ? "mic" : "mic-off"}
                  size={16}
                />
              </button>

              <AdminCanaryRoutingToggle
                value={canaryRouting}
                onChange={onChangeCanaryRouting}
              />

              <Button
                variant={isConnected ? "danger" : "success"}
                size="sm"
                outline
                onClick={isConnected ? onClickStop : onClickStart}
                disabled={!hasReleaseSelected || isStarting}
                className="ml-auto"
              >
                <Icon
                  name={isConnected ? "stop" : isStarting ? "loader" : "play"}
                  size={14}
                  className={isStarting ? "animate-spin" : undefined}
                />
                {isConnected ? "Stop" : isStarting ? "Starting..." : "Start"}
              </Button>
            </View>
          </View>

          {/* Connection Error */}
          {(connectionError || primVoicesError || startError) && (
            <Alert
              variant="destructive"
              className="mx-4 mt-3"
            >
              <AlertDescription>{connectionError || primVoicesError || startError}</AlertDescription>
            </Alert>
          )}

          {/* Empty state when env has no releases */}
          {environments.length > 0 && releasesInEnv.length === 0 && (
            <View className="flex flex-col items-center justify-center gap-2 p-6 text-center">
              <Icon
                name="rocket"
                size={24}
                className="text-gray-300 dark:text-gray-600"
              />
              <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">No releases in this environment</Text>
              <Text className="text-xs text-gray-400 dark:text-gray-500">
                Run <span className="font-mono text-gray-600 dark:text-gray-300">vr release {envName || "<env>"}</span> to create one.
              </Text>
            </View>
          )}

          {/* Empty state when org has no envs */}
          {environments.length === 0 && (
            <View className="flex flex-col items-center justify-center gap-2 p-6 text-center">
              <Icon
                name="layers"
                size={24}
                className="text-gray-300 dark:text-gray-600"
              />
              <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">No environments for this release</Text>
              <Text className="text-xs text-gray-400 dark:text-gray-500">
                Create an environment, then run <span className="font-mono text-gray-600 dark:text-gray-300">vr release &lt;env&gt;</span>.
              </Text>
            </View>
          )}

          {hasReleaseSelected && (
            <View className="flex-1 overflow-y-auto">
              <AgentMonitoring />
            </View>
          )}

          {/* Text Input (always visible) */}
          <View className="mt-auto p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <View className="relative flex w-full">
              <InputField
                value={inputMessage}
                onChange={setInputMessage}
                placeholder={isConnected ? "Type your message..." : "Start a session to type messages..."}
                className="flex-1 pr-12 h-14 min-w-0"
                onKeyDown={onKeyDown}
                readOnly={!isConnected}
              />
              <View className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <button
                  onClick={isConnected ? onClickSendMessage : undefined}
                  type="button"
                  disabled={!isConnected || !inputMessage.trim()}
                  className={clsx(
                    "flex items-center justify-center w-10 h-10 flex-shrink-0 rounded-lg transition-colors border",
                    !isConnected
                      ? "bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-40"
                      : !inputMessage.trim()
                        ? "bg-blue-50 dark:bg-blue-950/40 text-blue-400 dark:text-blue-500 border-blue-200 dark:border-blue-800 cursor-not-allowed opacity-60"
                        : "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50",
                  )}
                >
                  <Icon
                    name="send"
                    size={16}
                  />
                </button>
              </View>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

type ReleaseDebuggerProps = {
  agentId: string;
  isOpen: boolean;
  onClick: () => void;
};

export const ReleaseDebugger = ({ agentId, isOpen, onClick }: ReleaseDebuggerProps) => {
  const dispatch = useAppDispatch();
  const { organizationId } = useEffectiveOrganization();
  const allEnvironments = useAppSelector((state) => Object.values(state.environment.entities));
  const allReleases = useAppSelector((state) =>
    Object.values(state.release.entities).filter((r) => r.agentId === agentId && r.status === "released"),
  );
  const agentFunctionsById = useAppSelector((state) => state.agentFunction.entities);
  const debugRequest = useApiRequest<DebugSessionResponse>();

  const releasesByEnv = useMemo(() => {
    return allReleases.reduce<Record<string, Release[]>>((acc, r) => {
      if (!acc[r.environmentId]) acc[r.environmentId] = [];
      acc[r.environmentId].push(r);
      return acc;
    }, {});
  }, [allReleases]);

  const debuggableEnvs = useMemo(() => {
    const envIdsWithReleases = new Set(allReleases.map((r) => r.environmentId));
    return allEnvironments.filter((env) => envIdsWithReleases.has(env.id));
  }, [allEnvironments, allReleases]);

  const [environmentId, setEnvironmentId] = useState<string>("");
  const [releaseId, setReleaseId] = useState<string>("");

  // Fetch envs for the org
  useEffect(() => {
    if (organizationId) {
      dispatch(environmentActions.get({ organizationId }));
    }
  }, [organizationId, dispatch]);

  // Default env: first debuggable env
  useEffect(() => {
    if (debuggableEnvs.length === 0) {
      if (environmentId) setEnvironmentId("");
      return;
    }
    if (!environmentId || !debuggableEnvs.find((e) => e.id === environmentId)) {
      setEnvironmentId(debuggableEnvs[0].id);
    }
  }, [environmentId, debuggableEnvs]);

  const releasesInEnv = useMemo(() => {
    const list = environmentId ? releasesByEnv[environmentId] || [] : [];
    return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [environmentId, releasesByEnv]);

  // Default release: most recent in the selected env
  useEffect(() => {
    if (releasesInEnv.length === 0) {
      if (releaseId) setReleaseId("");
      return;
    }
    if (!releaseId || !releasesInEnv.find((r) => r.id === releaseId)) {
      setReleaseId(releasesInEnv[0].id);
    }
  }, [releasesInEnv, releaseId]);

  // PrimVoicesProvider's WebSocketClient copies its config at construction
  // (`this.config = { ...config }`), so mutating the original config after the
  // fact doesn't propagate. To trigger a fresh connection with a serverUrl
  // returned from /sessions/debug, we have to remount the Provider with a new
  // config object — `attempt.key` on Provider's `key` prop forces that
  // remount, and the new mount's autoConnect prop tells Internal to call
  // connect() in its useEffect once.
  // Super-admin blue/green pin, sent with every admin debug request:
  // true pins the session to the canary (inactive) color, false pins it to
  // the active color — both deterministic (no traffic-split roll). Non-admin
  // requests omit the field entirely (the API 400s it otherwise) and route
  // like live traffic. Resets to false on every mount.
  const { isAdmin } = useAuth();
  const [canaryRouting, setCanaryRouting] = useState(false);

  const [attempt, setAttempt] = useState<{ key: number; config: WebSocketClientConfig }>(() => ({
    key: 0,
    config: {
      apiUrl: API_URL,
      agentId,
      logLevel: "INFO",
      canary: canaryRouting,
      origin: "debugger",
    },
  }));
  const [startError, setStartError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const onRequestStart = async () => {
    if (!releaseId) return;
    setStartError(null);
    setIsStarting(true);
    try {
      const result = await debugRequest.fetch(`/v1/agents/${agentId}/sessions/debug`, {
        method: "POST",
        body: { releaseId, ...(isAdmin ? { canary: canaryRouting } : {}) },
      });
      if (!result.data) {
        setStartError(result.error?.message || "Failed to start session. Please try again.");
        return;
      }
      setAttempt((prev) => ({
        key: prev.key + 1,
        config: {
          apiUrl: API_URL,
          agentId,
          serverUrl: result.data!.data.url,
          customParameters: result.data!.data.parameters,
          logLevel: "INFO",
          canary: canaryRouting,
          origin: "debugger",
        },
      }));
    } catch (err: any) {
      setStartError(err?.message || "Failed to start session. Please try again.");
    } finally {
      setIsStarting(false);
    }
  };

  const engineColor = parseEngineColor(attempt.config.serverUrl);

  return (
    <PrimVoicesProvider
      key={attempt.key}
      config={attempt.config}
      autoConnect={!!attempt.config.serverUrl}
    >
      <ReleaseDebuggerInternal
        isOpen={isOpen}
        onClick={onClick}
        environments={debuggableEnvs}
        releasesInEnv={releasesInEnv}
        functionsById={agentFunctionsById}
        environmentId={environmentId}
        releaseId={releaseId}
        onChangeEnvironment={setEnvironmentId}
        onChangeRelease={setReleaseId}
        startError={startError}
        isStarting={isStarting}
        onRequestStart={onRequestStart}
        canaryRouting={canaryRouting}
        onChangeCanaryRouting={setCanaryRouting}
        engineColor={engineColor}
      />
    </PrimVoicesProvider>
  );
};
